import { useState, useEffect, useRef } from 'react'
import type { LeadRow } from '@/shared/types'
import { readStored, writeStored } from '@/shared/lib/storage'

const DISMISSED_KEY = 'focus.dismissedCallbacks'

function loadFired(): Set<string> {
  const raw = readStored(DISMISSED_KEY, { store: 'session' })
  return new Set(raw ? raw.split(',').filter(Boolean) : [])
}

interface CallbackPoller {
  callbackToast: LeadRow | null
  setCallbackToast: React.Dispatch<React.SetStateAction<LeadRow | null>>
}

// ── Callback-alert poller (RADAR-SE-022) ─────────────────────────────────
// Fires the CallbackToast when a lead with is_callback_alert=true reaches
// its committed nextContactAt moment. Visibility-aware so a backgrounded
// tab catches up the instant the BDR switches back. 24-hour catch-up
// window keeps a stale lead from re-pinging weeks after it was due (e.g.
// BDR was on vacation) while still surviving normal short throttling.
//
// Seen-this-session state lives in sessionStorage, not just the in-memory
// ref below — a ref alone forgets on reload, so a BDR who dismissed a
// reminder and then refreshed the page would see the exact same toast pop
// right back up. sessionStorage carries the same "don't ask again today"
// across a reload without also surviving into tomorrow's session.
export function useCallbackPoller(leads: LeadRow[], myEmail?: string): CallbackPoller {
  const [callbackToast, setCallbackToast] = useState<LeadRow | null>(null)
  const firedCallbacksRef = useRef<Set<string>>(loadFired())

  // A callback is a promise one person made, so only that person is reminded.
  // Focus is owned-only, but the Table is the whole pipeline — so on that tab
  // every seller was being reminded of every colleague's promises, and of
  // unclaimed leads nobody had promised anything about.
  const mine = (lead: LeadRow) =>
    !!myEmail && !!lead.assignedTo &&
    lead.assignedTo.toLowerCase() === myEmail.toLowerCase()

  useEffect(() => {
    const tick = () => {
      const now = Date.now()
      const cutoff = now - 24 * 60 * 60 * 1000
      for (const lead of leads) {
        if (!lead.isCallbackAlert) continue
        if (!mine(lead)) continue
        if (firedCallbacksRef.current.has(lead.id)) continue
        const dueAt = lead.nextContactAt ? Date.parse(lead.nextContactAt) : NaN
        if (Number.isNaN(dueAt)) continue
        if (dueAt > now) continue
        if (dueAt < cutoff) continue
        firedCallbacksRef.current.add(lead.id)
        writeStored(DISMISSED_KEY, Array.from(firedCallbacksRef.current).join(','), { store: 'session' })
        setCallbackToast(lead)
        return  // one toast at a time; next due lead surfaces after dismiss
      }
    }
    tick()
    const id = window.setInterval(tick, 30_000)
    const onVisible = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [leads, myEmail])

  return { callbackToast, setCallbackToast }
}
